import { useEffect, useRef, useCallback, useState } from 'react';
import { create } from 'zustand';
import { io, Socket } from 'socket.io-client';
import { useAuthStore } from '../store/auth';
import api from '../services/api';

// Notification types
export interface Notification {
  id: string;
  type: string;
  title: string;
  message: string;
  priority: 'high' | 'medium' | 'low';
  category: 'affiliate' | 'payment' | 'conversion' | 'fraud' | 'campaign' | 'system';
  data?: Record<string, any>;
  actionUrl?: string;
  isRead: boolean;
  createdAt: string;
  readAt?: string;
}

interface NotificationState {
  notifications: Notification[];
  unreadCount: number;
  isConnected: boolean;
  setNotifications: (notifications: Notification[]) => void;
  addNotification: (notification: Notification) => void;
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
  setUnreadCount: (count: number) => void;
  setConnected: (connected: boolean) => void;
  clearNotifications: () => void;
}

// Zustand store for notification state
export const useNotificationStore = create<NotificationState>((set) => ({
  notifications: [],
  unreadCount: 0,
  isConnected: false,
  setNotifications: (notifications) => set({ notifications }),
  addNotification: (notification) =>
    set((state) => ({
      notifications: [notification, ...state.notifications].slice(0, 100),
      unreadCount: state.unreadCount + 1,
    })),
  markAsRead: (id) =>
    set((state) => ({
      notifications: state.notifications.map((n) =>
        n.id === id ? { ...n, isRead: true, readAt: new Date().toISOString() } : n
      ),
      unreadCount: Math.max(0, state.unreadCount - 1),
    })),
  markAllAsRead: () =>
    set((state) => ({
      notifications: state.notifications.map((n) => ({
        ...n,
        isRead: true,
        readAt: n.readAt || new Date().toISOString(),
      })),
      unreadCount: 0,
    })),
  setUnreadCount: (count) => set({ unreadCount: count }),
  setConnected: (connected) => set({ isConnected: connected }),
  clearNotifications: () => set({ notifications: [], unreadCount: 0 }),
}));

// Sound for notifications
const playNotificationSound = (priority: 'high' | 'medium' | 'low') => {
  try {
    const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);

    switch (priority) {
      case 'high':
        oscillator.frequency.setValueAtTime(880, audioContext.currentTime);
        oscillator.frequency.setValueAtTime(1047, audioContext.currentTime + 0.1);
        gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);
        oscillator.start(audioContext.currentTime);
        oscillator.stop(audioContext.currentTime + 0.3);
        break;
      case 'medium':
        oscillator.frequency.setValueAtTime(659, audioContext.currentTime);
        gainNode.gain.setValueAtTime(0.2, audioContext.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.2);
        oscillator.start(audioContext.currentTime);
        oscillator.stop(audioContext.currentTime + 0.2);
        break;
      case 'low':
        oscillator.frequency.setValueAtTime(440, audioContext.currentTime);
        gainNode.gain.setValueAtTime(0.1, audioContext.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.15);
        oscillator.start(audioContext.currentTime);
        oscillator.stop(audioContext.currentTime + 0.15);
        break;
    }
  } catch (error) {
    console.log('Audio not available');
  }
};

// Main hook for socket connection
export function useNotifications() {
  const socketRef = useRef<Socket | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const maxReconnectAttempts = 5;

  const { accessToken, isAuthenticated } = useAuthStore();
  const {
    notifications,
    unreadCount,
    isConnected,
    setNotifications,
    addNotification,
    markAsRead,
    markAllAsRead,
    setUnreadCount,
    setConnected,
    clearNotifications,
  } = useNotificationStore();

  const [soundEnabled, setSoundEnabled] = useState(() => {
    const saved = localStorage.getItem('affiliateNotificationSoundEnabled');
    return saved !== 'false';
  });

  const toggleSound = useCallback(() => {
    setSoundEnabled((prev) => {
      const newValue = !prev;
      localStorage.setItem('affiliateNotificationSoundEnabled', String(newValue));
      return newValue;
    });
  }, []);

  // Fetch initial notifications
  const fetchNotifications = useCallback(async () => {
    try {
      const [unreadResponse, countResponse] = await Promise.all([
        api.get('/notifications/unread?limit=20'),
        api.get('/notifications/unread-count'),
      ]);

      if (unreadResponse.data.success) {
        setNotifications(unreadResponse.data.data);
      }
      if (countResponse.data.success) {
        setUnreadCount(countResponse.data.data.count);
      }
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    }
  }, [setNotifications, setUnreadCount]);

  // Mark notification as read
  const handleMarkAsRead = useCallback(
    async (id: string) => {
      try {
        await api.patch(`/notifications/${id}/read`);
        markAsRead(id);

        if (socketRef.current?.connected) {
          socketRef.current.emit('markAsRead', id);
        }
      } catch (error) {
        console.error('Failed to mark notification as read:', error);
      }
    },
    [markAsRead]
  );

  // Mark all as read
  const handleMarkAllAsRead = useCallback(async () => {
    try {
      await api.patch('/notifications/read-all');
      markAllAsRead();
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    }
  }, [markAllAsRead]);

  // Connect to socket
  useEffect(() => {
    if (!isAuthenticated || !accessToken) {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
        setConnected(false);
      }
      clearNotifications();
      return;
    }

    fetchNotifications();

    const socketUrl = window.location.origin;

    socketRef.current = io(socketUrl, {
      auth: { token: accessToken },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: maxReconnectAttempts,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
    });

    const socket = socketRef.current;

    socket.on('connect', () => {
      console.log('Socket connected');
      setConnected(true);
      reconnectAttemptsRef.current = 0;
    });

    socket.on('disconnect', (reason) => {
      console.log('Socket disconnected:', reason);
      setConnected(false);
    });

    socket.on('connect_error', (error) => {
      console.error('Socket connection error:', error);
      reconnectAttemptsRef.current++;
    });

    socket.on('notification', (notification: Notification) => {
      console.log('Received notification:', notification);
      addNotification(notification);

      if (soundEnabled) {
        playNotificationSound(notification.priority);
      }

      if (Notification.permission === 'granted') {
        new Notification(notification.title, {
          body: notification.message,
          icon: '/favicon.ico',
          tag: notification.id,
        });
      }
    });

    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, [isAuthenticated, accessToken, fetchNotifications, addNotification, setConnected, clearNotifications, soundEnabled]);

  const requestPermission = useCallback(async () => {
    if ('Notification' in window && Notification.permission === 'default') {
      await Notification.requestPermission();
    }
  }, []);

  return {
    notifications,
    unreadCount,
    isConnected,
    soundEnabled,
    toggleSound,
    markAsRead: handleMarkAsRead,
    markAllAsRead: handleMarkAllAsRead,
    refresh: fetchNotifications,
    requestPermission,
  };
}

// Hook for fetching all notifications with pagination
export function useAllNotifications() {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  });
  const [filters, setFilters] = useState<{
    category?: string;
    isRead?: boolean;
    priority?: string;
  }>({});

  const fetchNotifications = useCallback(
    async (page = 1) => {
      setLoading(true);
      try {
        const params = new URLSearchParams({
          page: String(page),
          limit: String(pagination.limit),
        });

        if (filters.category) params.append('category', filters.category);
        if (filters.isRead !== undefined) params.append('isRead', String(filters.isRead));
        if (filters.priority) params.append('priority', filters.priority);

        const response = await api.get(`/notifications?${params}`);

        if (response.data.success) {
          setNotifications(response.data.data);
          setPagination(response.data.pagination);
        }
      } catch (error) {
        console.error('Failed to fetch notifications:', error);
      } finally {
        setLoading(false);
      }
    },
    [pagination.limit, filters]
  );

  useEffect(() => {
    fetchNotifications(1);
  }, [filters, fetchNotifications]);

  const goToPage = useCallback(
    (page: number) => {
      fetchNotifications(page);
    },
    [fetchNotifications]
  );

  const updateFilters = useCallback((newFilters: typeof filters) => {
    setFilters(newFilters);
  }, []);

  return {
    notifications,
    loading,
    pagination,
    filters,
    goToPage,
    updateFilters,
    refresh: () => fetchNotifications(pagination.page),
  };
}
